import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import { Input } from '@/components/Forms';
import Text from '@/components/Text';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { VideoFactoryCategory } from '@/types/resources';
import { api } from '@/utils/api';
import { scrollBarStyles } from '@/utils/theme';

import CategoryCreationModal from './CategoryCreationModal';

const Dropdown = styled.div`
  position: relative;
  width: 220px;
`;

const Arrow = styled.svg<{ $opened: boolean }>`
  color: ${({ theme }) => theme.colors.gray};
  transition: transform 0.15s ease-out;

  ${({ $opened }) =>
    $opened &&
    css`
      transform: rotate(180deg);
    `}
`;

const Label = styled(Text)`
  flex: 1;
  padding: 0 8px;
  text-align: left;
`;

interface SelectionButtonProps {
  $opened: boolean;
}

const SelectionButton = styled.button.attrs({
  type: 'button',
})<SelectionButtonProps>`
  display: flex;
  align-items: center;
  width: 220px;
  max-width: 100%;
  height: 32px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  border: 1px solid
    ${({ theme, $opened }) => theme.colors[$opened ? 'gray' : 'lightGray']};
  padding: 0 12px;
  flex-shrink: 0;

  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }
`;

const Options = styled.div`
  position: absolute;
  box-shadow: 0px 0px 8px 0px #2222221f;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  z-index: 2;
  padding: 4px;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-height: 400px;
  overflow-y: auto;
  ${scrollBarStyles};

  ${SelectionButton} {
    border: 0;
    height: auto;
    padding-top: 12px;
    padding-bottom: 12px;

    &:hover {
      background-color: ${({ theme }) => theme.colors.grayscaleLight};
    }
  }
`;

const SearchInput = styled(Input)`
  border: 0;
  height: 100%;
  width: 100%;
  padding: 0;
  font-size: 12px;
  outline: none !important;
`;

const Wrap = styled.div`
  display: flex;
  gap: 12px;
`;

interface Props {
  activeCategory: null | VideoFactoryCategory;
  setActiveCategory: React.Dispatch<React.SetStateAction<VideoFactoryCategory>>;
}

const CategorySelection: FC<Props> = ({
  activeCategory,
  setActiveCategory,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [showCreationForm, setShowCreationForm] = useState(false);

  const [search, setSearch] = useState('');

  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery(
    ['video_factory_categories'],
    () =>
      api.get<never, VideoFactoryCategory[]>('/v1/video-factory/categories'),
    {
      staleTime: 120_000,
    },
  );

  const onSelect = (category: VideoFactoryCategory) => {
    setActiveCategory(category);
    setSearch('');
    setIsOpen(false);
  };

  const filteredPages = useMemo(() => {
    if (!search) {
      return data;
    }

    return data?.filter((p) =>
      p.name.toLowerCase().includes(search.toLowerCase()),
    );
  }, [search, data]);

  useOnClickOutside(dropdownRef, () => {
    setIsOpen(false);
    setSearch('');
  });

  return (
    <Wrap>
      <Dropdown>
        <SelectionButton
          $opened={isOpen}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
        >
          {!isOpen ? (
            <>
              <Label $type="smallHeadline" as="span">
                {activeCategory?.name ?? 'Default'}
              </Label>
              <Arrow
                $opened={isOpen}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M8 10.5L12 5.5H4L8 10.5Z" fill="currentColor" />
              </Arrow>
            </>
          ) : (
            <SearchInput
              placeholder="Search by name"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
        </SelectionButton>
        {isOpen && !!filteredPages?.length && (
          <Options ref={dropdownRef}>
            <SelectionButton
              onClick={() => onSelect({ name: 'DEFAULT', id: null })}
              key={'DEFAULT'}
              $opened={false}
            >
              <Label $type="smallHeadline" as="span">
                DEFAULT
              </Label>
            </SelectionButton>
            {filteredPages?.map((page) => (
              <SelectionButton
                onClick={() => onSelect(page)}
                key={page.id}
                $opened={false}
              >
                <Label $type="smallHeadline" as="span">
                  {page.name}
                </Label>
              </SelectionButton>
            ))}
          </Options>
        )}
      </Dropdown>
      <Button small onClick={() => setShowCreationForm(true)}>
        Add Category
      </Button>
      <AnimatePresence>
        {showCreationForm && (
          <CategoryCreationModal
            onClose={() => setShowCreationForm(false)}
            onCreated={setActiveCategory}
          />
        )}
      </AnimatePresence>
    </Wrap>
  );
};

export default CategorySelection;
